import React, { useState, useEffect } from 'react';
import { useChatContext } from '../contexts/ChatContext';

const GroupSettingsModal = ({ group, onClose }) => {
  const {
    currentUser,
    users,
    groupMembers,
    addGroupMember,
    removeGroupMember,
    deleteGroup,
    loadUsers,
  } = useChatContext();

  const [selectedNewMember, setSelectedNewMember] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isAdmin = group?.created_by === currentUser?.id;

  useEffect(() => {
    // Load users if not already loaded (for add member dropdown)
    if (users.length === 0) {
      loadUsers();
    }
  }, [users.length, loadUsers]);

  // Filter out users who are already members
  const availableUsers = users.filter(
    user => !groupMembers.some(member => member.user_id === user.id)
  );

  const handleAddMember = async () => {
    if (!selectedNewMember) return;

    setAdding(true);
    try {
      await addGroupMember(group.id, selectedNewMember);
      setSelectedNewMember('');
    } catch (err) {
      console.error('Failed to add member:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    setRemoving(userId);
    try {
      await removeGroupMember(group.id, userId);
    } catch (err) {
      console.error('Failed to remove member:', err);
    } finally {
      setRemoving(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setDeleting(true);
    try {
      await deleteGroup(group.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete group:', err);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="group-settings-overlay" onClick={handleOverlayClick}>
      <div className="group-settings-modal">
        <div className="group-settings-header">
          <div>
            <h3>{group.name}</h3>
            <div className="group-settings-subtitle">
              {groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="group-settings-content">
          {/* Members List */}
          <div className="group-members-section">
            <h4>Members</h4>
            {groupMembers.map(member => {
              const memberIsAdmin = member.role === 'admin';
              const isSelf = member.user_id === currentUser?.id;

              return (
                <div key={member.user_id} className="group-member-item">
                  <div className="group-member-avatar">
                    {member.first_name?.charAt(0) || member.user_name?.charAt(0) || '?'}
                  </div>
                  <div className="group-member-info">
                    <div className="group-member-name">
                      {member.first_name && member.last_name
                        ? `${member.first_name} ${member.last_name}`
                        : member.user_name}
                      {isSelf && ' (You)'}
                    </div>
                    <div className="group-member-role">
                      @{member.user_name}
                    </div>
                  </div>
                  {memberIsAdmin && (
                    <span className="group-member-admin-badge">Admin</span>
                  )}
                  {isAdmin && !memberIsAdmin && !isSelf && (
                    <button
                      className="group-member-remove-btn"
                      onClick={() => handleRemoveMember(member.user_id)}
                      disabled={removing === member.user_id}
                    >
                      {removing === member.user_id ? '...' : 'Remove'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add Member Section - Admin Only */}
          {isAdmin && availableUsers.length > 0 && (
            <div className="add-member-section">
              <h4>Add Member</h4>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  className="add-member-select"
                  value={selectedNewMember}
                  onChange={(e) => setSelectedNewMember(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.username}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  onClick={handleAddMember}
                  disabled={!selectedNewMember || adding}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {adding ? '...' : 'Add'}
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone - Admin Only */}
          {isAdmin && (
            <div className="group-danger-zone">
              <h4>Danger Zone</h4>
              <button
                className="btn-danger"
                onClick={handleDeleteGroup}
                disabled={deleting}
              >
                {deleting
                  ? 'Deleting...'
                  : confirmDelete
                    ? 'Click again to confirm'
                    : 'Delete Group'}
              </button>
            </div>
          )}
        </div>

        <div className="group-settings-actions">
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupSettingsModal;
